/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './SidebarList';

const SidebarListManager: ComponentManagerFn<'SidebarList'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SidebarList'>(platform, 'SidebarList'),
    type: 'SidebarList',
    name: 'Sidebar List',
    isChildAllowed: (page, item, childType) =>
      (['SidebarListItem'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['SidebarListItem', 'SidebarListItem'],
    props: {},
    getDisplayText: (item) => '',
    category: 'Sidebar',
    canHaveChildren: () => true,
    icon: { iconName: 'list' },
    description: `**Overview**<br/>
The Sidebar List is a container component designed to organize and display a vertical sequence of items within a Sidebar Panel. It serves as the structural wrapper for individual list entries, ensuring consistent spacing and alignment for navigation menus or option lists.
Unlike the SidebarDraggableList which connects to a Data Store, this component is typically used for static lists where you manually define each item.

**1. Structure & Children**<br/>
This component functions exclusively as a parent container for specific list items.
* **Allowed Children:** It strictly accepts SidebarListItem components as children. This restriction enforces a valid list structure.
* **Default Content:** When added to a page, it automatically populates with two SidebarListItem children to provide a starting template.

**2. Configuration**<br/>
* **Zero Configuration:** The container itself has no specific properties (props: {}). All customization (icons, labels, actions) happens on the individual SidebarListItem children that you add inside it.

**3. Best Use Cases**<br/>
* **Main Navigation:** Building a static app menu (e.g., "Home", "Profile", "Settings").
* **Quick Actions:** Creating a list of common tasks in a side panel (e.g., "New Order", "Print Invoice", "Email Support").
* **Sectioned Lists:** Using multiple Sidebar List components separated by headers to group related links (e.g., "Favorites" list vs. "Recent" list).`,
  };
};

export default SidebarListManager;
